import React, { useEffect, useState } from 'react';
import { Tag, Loader2, RefreshCw, Save, Plus, Trash2, ToggleLeft, ToggleRight, Crown } from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface PricingPlan {
  id: string;
  name: string;
  price_cents: number;
  period: string;
  duration_months: number;
  description: string;
  checkout_url: string;
  badge: string | null;
  is_highlighted: boolean;
  is_active: boolean;
  sort_order: number;
  features: any;
  discount_percent: number | null;
  trial_days: number;
  created_at: string;
  updated_at: string;
}

const emptyPlan: Partial<PricingPlan> = {
  name: '', price_cents: 0, period: '/Monat', duration_months: 1,
  description: '', checkout_url: '', badge: null, is_highlighted: false,
  is_active: true, sort_order: 0, features: [], discount_percent: null, trial_days: 0,
};

export const PricingPage: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<PricingPlan>>({});
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState<Partial<PricingPlan>>({ ...emptyPlan });

  useEffect(() => { loadPlans(); }, []);

  const loadPlans = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('pricing_plans')
      .select('*')
      .order('sort_order', { ascending: true });
    setPlans(data || []);
    setLoading(false);
  };

  const savePlan = async (id: string) => {
    setSaving(true);
    const original = plans.find(p => p.id === id);
    const { error } = await adminDb.from('pricing_plans').update(form).eq('id', id);
    if (error) { alert(`Fehler: ${error.message}`); setSaving(false); return; }
    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'pricing_plan.update',
      resource_type: 'pricing_plan',
      resource_id: id,
      old_value: original ? { name: original.name, price_cents: original.price_cents } : null,
      new_value: { ...form },
    });
    setEditingId(null); setForm({});
    await loadPlans();
    setSaving(false);
  };

  const createPlan = async () => {
    setSaving(true);
    const { data, error } = await adminDb.from('pricing_plans').insert([newPlan]).select().single();
    if (error) { alert(`Fehler: ${error.message}`); setSaving(false); return; }
    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'pricing_plan.create',
      resource_type: 'pricing_plan',
      resource_id: data?.id || 'unknown',
      new_value: { name: newPlan.name, price_cents: newPlan.price_cents },
    });
    setCreating(false); setNewPlan({ ...emptyPlan });
    await loadPlans();
    setSaving(false);
  };

  const deletePlan = async (id: string) => {
    const plan = plans.find(p => p.id === id);
    if (!confirm(`Plan "${plan?.name}" wirklich löschen?`)) return;
    await adminDb.from('pricing_plans').delete().eq('id', id);
    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'pricing_plan.delete',
      resource_type: 'pricing_plan',
      resource_id: id,
      old_value: { name: plan?.name, price_cents: plan?.price_cents },
    });
    await loadPlans();
  };

  const toggleActive = async (plan: PricingPlan) => {
    await adminDb.from('pricing_plans').update({ is_active: !plan.is_active }).eq('id', plan.id);
    const session = await supabase.auth.getSession();
    await adminDb.from('audit_logs').insert({
      admin_email: session.data.session?.user?.email || 'unknown',
      action: 'pricing_plan.toggle_active',
      resource_type: 'pricing_plan',
      resource_id: plan.id,
      old_value: { is_active: plan.is_active },
      new_value: { is_active: !plan.is_active },
    });
    await loadPlans();
  };

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2).replace('.', ',')}`;

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Tag size={24} /> Preise & Pakete</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  const activePlans = plans.filter(p => p.is_active).length;
  const avgPrice = plans.length > 0 ? plans.reduce((s, p) => s + p.price_cents, 0) / plans.length / 100 : 0;

  return (
    <div>
      <div className="page-header">
        <h1><Tag size={24} /> Preise & Pakete</h1>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={loadPlans}><RefreshCw size={14} /> Aktualisieren</button>
          <button className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={14} /> Neuer Plan</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Pläne gesamt</span>
          <span className="kpi-value">{plans.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Aktive Pläne</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>{activePlans}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ø Preis</span>
          <span className="kpi-value">€{avgPrice.toFixed(0)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Hervorgehoben</span>
          <span className="kpi-value">{plans.filter(p => p.is_highlighted).length}</span>
        </div>
      </div>

      {/* Create new plan */}
      {creating && (
        <div className="card mb-4" style={{ padding: 20, border: '2px solid var(--primary)' }}>
          <h3 style={{ marginBottom: 16 }}>Neuen Plan erstellen</h3>
          <div className="grid-cols-3 mb-3" style={{ gap: 12 }}>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Name</label>
              <input className="input" value={newPlan.name || ''} onChange={e => setNewPlan(p => ({ ...p, name: e.target.value }))} placeholder="z.B. 3 Monate" />
            </div>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Preis (€)</label>
              <input className="input" type="number" step="0.01" value={(newPlan.price_cents || 0) / 100} onChange={e => setNewPlan(p => ({ ...p, price_cents: Math.round(parseFloat(e.target.value) * 100) }))} />
            </div>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Zeitraum</label>
              <select className="input" value={newPlan.period || '/Monat'} onChange={e => setNewPlan(p => ({ ...p, period: e.target.value }))}>
                <option value="/Monat">/Monat</option>
                <option value="einmalig">einmalig</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Laufzeit (Monate)</label>
              <input className="input" type="number" value={newPlan.duration_months || 1} onChange={e => setNewPlan(p => ({ ...p, duration_months: parseInt(e.target.value) }))} />
            </div>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Beschreibung</label>
              <input className="input" value={newPlan.description || ''} onChange={e => setNewPlan(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 4 }}>Sortierung</label>
              <input className="input" type="number" value={newPlan.sort_order || 0} onChange={e => setNewPlan(p => ({ ...p, sort_order: parseInt(e.target.value) }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={createPlan} disabled={saving || !newPlan.name}>
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Erstellen
            </button>
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Plans table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Preis</th>
              <th>Zeitraum</th>
              <th>Laufzeit</th>
              <th>Badge</th>
              <th>Status</th>
              <th>Rabatt</th>
              <th>Testtage</th>
              <th>Checkout URL</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => {
              const isEditing = editingId === plan.id;
              return (
                <tr key={plan.id} style={{ opacity: plan.is_active ? 1 : 0.5 }}>
                  <td>
                    {isEditing ? (
                      <input className="input" value={form.name ?? plan.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: 140 }} />
                    ) : (
                      <div>
                        <div style={{ fontWeight: 600 }}>{plan.name}</div>
                        <div className="text-xs text-muted">{plan.description}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" type="number" step="0.01" value={(form.price_cents ?? plan.price_cents) / 100} onChange={e => setForm(f => ({ ...f, price_cents: Math.round(parseFloat(e.target.value) * 100) }))} style={{ width: 80 }} />
                    ) : (
                      <span style={{ fontWeight: 700 }}>{formatPrice(plan.price_cents)}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select className="input" value={form.period ?? plan.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} style={{ width: 100 }}>
                        <option value="/Monat">/Monat</option>
                        <option value="einmalig">einmalig</option>
                      </select>
                    ) : plan.period}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" type="number" value={form.duration_months ?? plan.duration_months} onChange={e => setForm(f => ({ ...f, duration_months: parseInt(e.target.value) }))} style={{ width: 60 }} />
                    ) : `${plan.duration_months} Mon.`}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" value={form.badge ?? plan.badge ?? ''} onChange={e => setForm(f => ({ ...f, badge: e.target.value || null }))} placeholder="z.B. Beliebt" style={{ width: 100 }} />
                    ) : (
                      plan.badge ? <span className="badge badge-info">{plan.badge}</span> : <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <button onClick={() => toggleActive(plan)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      {plan.is_active
                        ? <ToggleRight size={22} color="var(--success)" />
                        : <ToggleLeft size={22} color="var(--text-muted)" />
                      }
                    </button>
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" type="number" value={form.discount_percent ?? plan.discount_percent ?? ''} onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value ? parseInt(e.target.value) : null }))} style={{ width: 60 }} />
                    ) : (
                      plan.discount_percent ? `${plan.discount_percent}%` : '—'
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" type="number" value={form.trial_days ?? plan.trial_days} onChange={e => setForm(f => ({ ...f, trial_days: parseInt(e.target.value) }))} style={{ width: 60 }} />
                    ) : (plan.trial_days > 0 ? `${plan.trial_days}d` : '—')}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="input" value={form.checkout_url ?? plan.checkout_url} onChange={e => setForm(f => ({ ...f, checkout_url: e.target.value }))} placeholder="https://..." style={{ width: 160, fontFamily: 'monospace', fontSize: 11 }} />
                    ) : (
                      <span className="text-xs text-muted" style={{ fontFamily: 'monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }}>
                        {plan.checkout_url || '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {isEditing ? (
                        <>
                          <button className="btn btn-primary btn-sm" onClick={() => savePlan(plan.id)} disabled={saving}>
                            {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setEditingId(null); setForm({}); }}>✕</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setEditingId(plan.id); setForm({}); }}>Bearbeiten</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deletePlan(plan.id)}><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {plans.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Noch keine Preispläne. Klicke auf "Neuer Plan" um einen zu erstellen.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card mt-4" style={{ padding: 16, borderLeft: '3px solid var(--warning)' }}>
        <p className="text-sm"><strong>💡 Hinweis:</strong> Änderungen an den Preisplänen werden sofort in der App für neue Besucher sichtbar. Preise werden in <strong>Cent</strong> gespeichert und automatisch in Euro umgerechnet.</p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
